import { useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, useHistory } from 'quartz-routing/react-router';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { View } from 'foundations-components/components/View';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import LessonInfoTabs from '../LessonPage/LessonInfoTabs';
import StoredContentContainer from '../../containers/StoredContentContainer';
import TrackContentWithInteractiveEducation from './TrackContentWithInteractiveEducation';
import { PRE_ASSESSMENT_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../../constants/Navigation';
import { SCREENS } from '../../constants/Tracking';
import { HasGate } from '../../features/accounts/components/HasGate';
import { useLessonQuizMe, useLessonQuizMeVideoRequestParams } from '../../features/interactiveEducation/hooks/useLessonQuizMe';
import { LessonPreQuizAlert } from '../../features/preAssessmentQuiz/components/LessonPreQuizAlert';
import { LessonPreQuizContainer } from '../../features/preAssessmentQuiz/components/LessonPreQuizContainer';
import { usePlaylistIdFromSearch } from '../../features/playlistContent/hooks';
import { OptionalToolRecommendationSection } from '../../features/toolRecommendations';
import { getNextContentUrl } from '../../features/tracks/tracksRouting';
import { isVideoAsset } from '../../features/tracks/tracksUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TrackPage = ({
  track,
  trackProgress,
  onAdvanceContent,
  onSkipPreAssessment,
  onStartLessonWithPreAssessmentFocus,
  lesson,
  lessonProgress,
  content,
  contentFragment,
  contentProgress
}) => {
  var _lesson$useCases;
  const {
    push
  } = useHistory();
  const {
    isNotLaptop
  } = useMediaQuery();
  const isInPlaylist = !!usePlaylistIdFromSearch();
  const {
    allVideosHaveCaptions,
    loading: captionsLoading
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson === null || lesson === void 0 ? void 0 : lesson.content
  });
  const {
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    regenerate: regenerateQuiz,
    regenerateSingleVideoQuiz
  } = useLessonQuizMe({
    lesson
  });
  const lessonQuizProps = useMemo(() => ({
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    regenerateQuiz,
    regenerateSingleVideoQuiz
  }), [captionsExistMap, generateQuizLoading, generateQuizError, regenerateQuiz, regenerateSingleVideoQuiz]);
  const videoCount = (lesson === null || lesson === void 0 ? void 0 : lesson.content.filter(isVideoAsset).length) || 0;
  const preAssessmentAvailable = isNotLaptop && !captionsLoading && allVideosHaveCaptions && videoCount > 1;
  const handlePreAssessmentClick = () => lesson && push(`/tracks/${track.id}/${lesson.id}/${PRE_ASSESSMENT_URL_FRAGMENT}`);
  const nextContentUrl = lesson ? getNextContentUrl({
    track,
    lesson,
    contentId: lesson.content[lesson.content.length - 1].id,
    isInPlaylist
  }) : undefined;
  const sectionViewProps = {
    backgroundColor: 'fill-surface-default',
    borderColor: 'border-core-subtle',
    borderRadius: '300',
    borderStyle: 'solid',
    borderWidth: '100',
    overflow: 'hidden'
  };
  if (lesson && contentFragment === PRE_ASSESSMENT_URL_FRAGMENT) return /*#__PURE__*/_jsxs(HasGate, {
    fallback: /*#__PURE__*/_jsx(Redirect, {
      to: `/tracks/${track.id}`
    }),
    gate: "Academy:LessonPreAssessment",
    children: [/*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
      children: /*#__PURE__*/_jsx(LessonPreQuizContainer, {
        lesson: lesson,
        onSkip: onSkipPreAssessment,
        onStartLessonWithPreAssessmentFocus: onStartLessonWithPreAssessmentFocus,
        screen: SCREENS.TRACK,
        track: track
      })
    })), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
      marginBlockStart: "600",
      children: /*#__PURE__*/_jsx(LessonInfoTabs, {
        content: content,
        lesson: lesson
      })
    }))]
  });
  if (contentFragment === SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS && lesson) return /*#__PURE__*/_jsxs(_Fragment, {
    children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
      gate: "Academy:LessonPreAssessment",
      children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
        lesson: lesson,
        onClick: handlePreAssessmentClick,
        screen: SCREENS.TRACK,
        track: track
      })
    }), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
      children: /*#__PURE__*/_jsx(OptionalToolRecommendationSection, {
        contentName: lesson.name,
        nextContentUrl: nextContentUrl,
        useCases: (_lesson$useCases = lesson.useCases) === null || _lesson$useCases === void 0 ? void 0 : _lesson$useCases.map(readUnhandledEnumCase)
      })
    })), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
      marginBlockStart: "600",
      children: /*#__PURE__*/_jsx(LessonInfoTabs, {
        lesson: lesson
      })
    }))]
  });
  if (lesson && content && isVideoAsset(content)) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
        gate: "Academy:LessonPreAssessment",
        children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
          lesson: lesson,
          onClick: handlePreAssessmentClick,
          screen: SCREENS.TRACK,
          track: track
        })
      }), /*#__PURE__*/_jsx(TrackContentWithInteractiveEducation, {
        lesson: lesson,
        lessonQuizProps: lessonQuizProps,
        onAdvanceContent: onAdvanceContent,
        track: track,
        videoContent: content
      }, content.id), /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        marginBlockStart: "600",
        children: /*#__PURE__*/_jsx(LessonInfoTabs, {
          content: content,
          lesson: lesson
        })
      }))]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [preAssessmentAvailable && /*#__PURE__*/_jsx(HasGate, {
      gate: "Academy:LessonPreAssessment",
      children: /*#__PURE__*/_jsx(LessonPreQuizAlert, {
        lesson: lesson,
        onClick: handlePreAssessmentClick,
        screen: SCREENS.TRACK,
        track: track
      })
    }), content ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        children: /*#__PURE__*/_jsx(StoredContentContainer, {
          content: content,
          contentProgress: contentProgress,
          lesson: lesson,
          lessonProgress: lessonProgress,
          onFinish: onAdvanceContent,
          track: track,
          trackProgress: trackProgress
        })
      })), lesson && /*#__PURE__*/_jsx(View, Object.assign({}, sectionViewProps, {
        marginBlockStart: "600",
        children: /*#__PURE__*/_jsx(LessonInfoTabs, {
          content: content,
          lesson: lesson
        })
      }))]
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "COURSE_VIEW_CONTENT_MISSING_ERROR",
      children: /*#__PURE__*/_jsx(ACMissingContentError, {})
    })]
  });
};
export default TrackPage;